// ABC chord symbols ("Am7", "Bbmaj7/D", "F#m7b5", "G7(b9)") → the app's chord
// vocabulary. ABC leaves chord text free-form, so the quality is transliterated
// into iReal's vocabulary (m7 → -7, maj7 → ^7, dim → o, ø → h) — the one every
// other importer, the chart views and the iReal exporter already speak — and
// mapped onto the catalogue by the same `mapQuality` the iReal parser uses.
//
// Text that does not read as a chord ("Coda", "Fine", "Bridge") is refused,
// so the caller can treat it as an annotation, which is how ABC 2.0 read any
// quoted string that was not a chord.

import type { ChordTypeId } from '../chords';
import { mapQuality } from '../ireal/chordParser';

export interface AbcChordSymbol {
  root: string;
  /** iReal-vocabulary quality: '' (major triad), '-7', '^7', 'o7', 'h7'… */
  quality: string;
  chordType: ChordTypeId;
  bass?: string;
}

export type AbcChordReading =
  | { kind: 'chord'; chord: AbcChordSymbol; parenthesized: boolean }
  | { kind: 'nc' };

/** What iReal's own chord grammar admits in a quality. */
const IREAL_QUALITY = /^(?:6\/9|sus|alt|add|[+\-^o\dhb#])*$/;

function accidental(text: string): string {
  return text.replace(/♭/g, 'b').replace(/♯/g, '#');
}

/** Transliterate an ABC quality into iReal's vocabulary, or null if it is not one. */
export function abcQuality(raw: string): string | null {
  let s = accidental(raw.normalize('NFC')).replace(/[\s(),]/g, '');
  s = s.replace(/[Δ∆△]/g, '^').replace(/°/g, 'o').replace(/ø/g, 'h');
  const lead: [RegExp, string][] = [
    // m(maj7), mM7, m^7, -maj7: minor with a major seventh.
    [/^(?:min|mi|m|-)(?:major|maj|Maj|MAJ|ma|M|\^)/, '-^'],
    [/^(?:major|maj|Maj|MAJ|ma|Ma)(?=[\d^#b]|add|sus)/, '^'],
    [/^M(?=[\d^#b]|add|sus)/, '^'],
    // A bare "M"/"maj" is the major triad.
    [/^(?:major|maj|Maj|MAJ|M)$/, ''],
    [/^(?:minor|min|mi|m|-)/, '-'],
    [/^(?:dim|o)/, 'o'],
    [/^(?:aug|\+)/, '+'],
    [/^h/, 'h'],
  ];
  let head = '';
  for (const [re, replacement] of lead) {
    const m = re.exec(s);
    if (m) {
      head = replacement;
      s = s.slice(m[0].length);
      break;
    }
  }
  const rest = s
    .replace(/major|maj|Maj|MAJ|M/g, '^')
    .replace(/aug/g, '+')
    .replace(/dim/g, 'o')
    .replace(/6\/9/g, '69');
  const quality = head + rest;
  return IREAL_QUALITY.test(quality) ? quality : null;
}

const ROOT = /^([A-G])([#b♯♭]?)(.*)$/;

function parseOne(text: string): AbcChordSymbol | null {
  const m = ROOT.exec(text);
  if (!m) return null;
  let body = m[3];
  let bass: string | undefined;
  const slash = /\/([A-G])([#b♯♭]?)$/.exec(body);
  if (slash) {
    bass = `${slash[1]}${accidental(slash[2])}`;
    body = body.slice(0, slash.index);
  }
  const quality = abcQuality(body);
  if (quality === null) return null;
  return {
    root: `${m[1]}${accidental(m[2])}`,
    quality,
    chordType: mapQuality(quality),
    ...(bass ? { bass } : {}),
  };
}

/**
 * Read one quoted ABC chord symbol. A symbol wrapped whole in parentheses is
 * an alternate or optional chord; "N.C." is no chord.
 */
export function parseAbcChordSymbol(raw: string): AbcChordReading | null {
  const text = raw.trim();
  if (!text) return null;
  if (/^(?:N\.?\s*C\.?|no\s*chord)$/i.test(text)) return { kind: 'nc' };
  const wrapped = /^\((.*)\)$/.exec(text);
  const inner = wrapped ? wrapped[1].trim() : text;
  const chord = parseOne(inner);
  return chord ? { kind: 'chord', chord, parenthesized: !!wrapped } : null;
}
